// src/components/facilityTransfer/FacilityTransferCard.jsx
//
// "Transférer au gérant": the facility's current owner, its pending
// invitation (Renvoyer / Annuler) and the form to invite the real owner.
// Used by the field app's prospect card, the commercial facility page and
// the Manager board - for admins and the facility's ambassador only (the
// backend refuses anyone else).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Paper, Typography, Stack, TextField, Button, Alert, Box } from '@mui/material';
import { listTransfers, startTransfer, resendTransfer, cancelTransfer, transferErrorText } from '../../utils/facilityTransferApi';

const day = (d) => new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });

export default function FacilityTransferCard({ facilityId, ownerEmail, getToken: getTokenProp, onChange, elevation }) {
  // Callers may pass a new function each render: only the facility reloads the list.
  const tokenRef = useRef(getTokenProp);
  tokenRef.current = getTokenProp;
  const getToken = useCallback(() => tokenRef.current(), []);
  const [transfers, setTransfers] = useState(null);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const load = useCallback(() => listTransfers(facilityId, getToken())
    .then((r) => setTransfers(r.transfers)).catch((e) => setError(transferErrorText(e))), [facilityId, getToken]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, done) => {
    setBusy(true); setError(''); setInfo('');
    try {
      const r = await fn();
      if (r && r.emailSent === false) setInfo('Invitation enregistrée, mais l’e-mail n’a pas pu partir : utilisez « Renvoyer ».');
      else if (done) setInfo(done);
      await load();
      onChange?.();
    } catch (e) { setError(transferErrorText(e)); } finally { setBusy(false); }
  };

  const pending = transfers?.find((t) => t.status === 'PENDING');
  const accepted = transfers?.find((t) => t.status === 'ACCEPTED');
  const invite = () => run(() => startTransfer(facilityId, email.trim(), getToken()), `Invitation envoyée à ${email.trim()}.`)
    .then(() => setEmail(''));
  const resend = () => run(() => resendTransfer(pending.id, getToken()), `Nouveau lien envoyé à ${pending.toEmail}.`);
  const cancel = () => {
    if (!window.confirm(`Annuler l’invitation envoyée à ${pending.toEmail} ? Son lien ne fonctionnera plus.`)) return;
    run(() => cancelTransfer(pending.id, getToken()), 'Invitation annulée.');
  };

  return (
    <Paper variant={elevation ? 'elevation' : 'outlined'} sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Propriétaire</Typography>
      {ownerEmail && <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-all' }}>{ownerEmail}</Typography>}
      {accepted && !pending && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Transféré à {accepted.toEmail} le {day(accepted.acceptedAt)}.
        </Typography>
      )}
      {error && <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setInfo('')}>{info}</Alert>}

      {pending ? (
        <Box>
          <Alert severity="info" sx={{ mb: 1 }}>
            Invitation envoyée à <b>{pending.toEmail}</b>, en attente jusqu’au {day(pending.expiresAt)}.
            L’établissement lui appartiendra dès qu’il aura accepté.
          </Alert>
          <Stack direction="row" spacing={1}>
            <Button size="small" disabled={busy} onClick={resend}>Renvoyer</Button>
            <Button size="small" color="error" disabled={busy} onClick={cancel}>Annuler l’invitation</Button>
          </Stack>
        </Box>
      ) : (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Transférer au gérant : il reçoit un e-mail pour accepter l’établissement avec son compte (créé au besoin).
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField size="small" type="email" fullWidth label="E-mail du gérant" value={email}
              onChange={(e) => setEmail(e.target.value)} inputProps={{ autoCapitalize: 'none', autoCorrect: 'off' }} />
            <Button variant="contained" disabled={busy || !email.trim()} onClick={invite} sx={{ whiteSpace: 'nowrap' }}>
              Transférer
            </Button>
          </Stack>
        </Box>
      )}
    </Paper>
  );
}
